import { Popover } from '@/components/builder/inspector-controls';
import { preflight, summarize } from '@/components/builder/preflight';
import { cn } from '@/lib/cn';
import type { AuthoringDoc } from '@personalizehq/editor';
import { CircleCheck, TriangleAlert } from 'lucide-react';
import { useMemo } from 'react';

// The publish gate, shown BEFORE you publish. `layout()` is pure, so the whole sweep — every step ×
// every conformance viewport, including 1.5× and 2× font scale — is a loop that runs on every edit.
//
// "Show me where 2× font scale breaks" is a question no competitor's editor can answer, because none
// of them can run their renderer without a device. We can, because we own the solver.

export function PreflightPanel({
  doc,
  onGo,
}: {
  doc: AuthoringDoc;
  /** Jump to the step that broke, and select the block that broke it. A report you cannot act on is
   *  a report you learn to ignore. */
  onGo: (stepId: string, nodeId?: string | undefined) => void;
}): React.JSX.Element {
  // Recomputed on every edit — 5 viewports × N steps of a pure function bounded by
  // MAX_LAYOUT_NODES=1024. ponytail: no memo cache beyond this, no worker. Add one if a profile says
  // so; a 3-step document is well under a millisecond.
  const rows = useMemo(() => summarize(preflight(doc)), [doc]);
  const clean = rows.length === 0;

  return (
    <Popover
      label="preflight"
      trigger={
        <>
          {clean ? (
            <CircleCheck className="size-3.5 text-emerald-500" />
          ) : (
            <TriangleAlert className="size-3.5 text-amber-500" />
          )}
          <span className={cn('text-[12px]', clean ? 'text-muted-foreground' : 'text-amber-500')}>
            {clean ? 'Ready' : `${rows.length} to fix`}
          </span>
        </>
      }
    >
      {clean ? (
        <p className="text-[12px] text-muted-foreground">
          Publishes clean — at every viewport, including 2&times; font scale.
        </p>
      ) : (
        <ul className="max-h-[280px] space-y-2.5 overflow-y-auto">
          {rows.map((row) => (
            <li key={row.key} className="space-y-0.5">
              {/* WHICH STEP. It was on the issue all along and never shown — so a three-step document
                  said "the fullscreen step only covers 589pt" and left the author to go and find
                  which one. Clicking it goes there, and selects the block that broke. */}
              <button
                type="button"
                onClick={() => onGo(row.stepId, row.nodeId)}
                className="text-left text-[11px] font-medium text-indigo-400 hover:underline"
              >
                {row.stepName}
                {row.nodeId ? <span className="text-muted-foreground"> · {row.nodeId}</span> : null}
              </button>
              <p className="text-[12px]">{row.message}</p>
              {row.viewports.length > 0 ? (
                // The viewports are the report. "This card overflows" said five times is noise;
                // "this card overflows at 1.5× and 2×" is a bug report with a repro.
                <p className="font-mono text-[10px] text-muted-foreground">{row.viewports.join(' · ')}</p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Popover>
  );
}
